import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpHandlerFn,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
  null
);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Skip adding token for refresh token and login endpoints
  if (isAuthUrl(req.url)) {
    return next(req);
  }

  // Add auth token to request if available
  const token = authService.getToken();
  if (token) {
    req = addTokenToRequest(req, token);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const errorMessage = error.error?.message || '';
        
        // Check if it's a session conflict
        if (errorMessage.includes('Session invalid - user logged in elsewhere')) {
          handleSessionConflict(authService);
          return throwError(() => error);
        } else if (errorMessage.includes('Session expired')) {
          handleSessionExpired(authService);
          return throwError(() => error);
        } else {
          return handle401Error(req, next, authService);
        }
      }
      return throwError(() => error);
    })
  );
};

function isAuthUrl(url: string): boolean {
  const authUrls = [
    `${environment.baseUrl}/auth/signin`,
    `${environment.baseUrl}/auth/refresh`,
    `${environment.baseUrl}/auth/publicKey`,
    `${environment.baseUrl}/auth/forgot-password`,
    `${environment.baseUrl}/auth/reset-password`,
  ];
  return authUrls.some((authUrl) => url.includes(authUrl));
}

function addTokenToRequest(
  request: HttpRequest<any>,
  token: string
): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((success: boolean): Observable<HttpEvent<any>> => {
        isRefreshing = false;

        if (success) {
          refreshTokenSubject.next(true);
          const token = authService.getToken();
          return next(addTokenToRequest(request, token!));
        }

        // If refresh failed, logout and redirect to login
        authService.logout();
        return throwError(
          () => new Error('Session expired. Please login again.')
        );
      }),
      catchError((err) => {
        isRefreshing = false;
        authService.logout();
        return throwError(() => err);
      })
    );
  } else {
    // Wait for token refresh completion
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((): Observable<HttpEvent<any>> => {
        const token = authService.getToken();
        return next(addTokenToRequest(request, token!));
      })
    );
  }
}

function handleSessionConflict(authService: AuthService): void {
  // Show specific message for session conflict
  console.warn('Session conflict detected - user logged in elsewhere');
  authService.showSessionConflictDialog();
  authService.logout(false); // Don't call backend logout as session is already invalid
}

function handleSessionExpired(authService: AuthService): void {
  // Show session expired message
  console.warn('Session expired');
  authService.showSessionExpiredMessage();
  authService.logout(false);
}
